import { EnumSportType } from '@/core/oddsApi/oddsApiType';

enum PlayerType {
    B2B,
    B2C,
    B2B2C,
}

export interface ICustomerSettings {
    isEFootballEnabled: boolean;
    isSimulationEnabled: boolean;
    isCricketEnabled: boolean;
    playerType: PlayerType;
    isInEFootballLeagueBlackList: boolean;
}

export interface IAdditionalSettings {
    filteredEFootballLeagueIds: number[];
}

interface IOutright {
    isWalkinOnly: boolean;
    tournament: {
        sport: {
            sportType: EnumSportType;
        };
    };
}

interface IOutrightTournament {
    sport: {
        sportType: EnumSportType;
    };
}

interface IEvent {
    isSimulation: boolean;
    tournament?: {
        sport?: {
            sportType: EnumSportType;
        } | null;
        id?: number;
    } | null;
}

interface IEventInfo {
    eventCount: number;
    simulationEventCount: number;
    filterEFootballEventCount: number;
}
interface ILeague {
    id: number;
    isSimulation: boolean;
    sport?: {
        sportType: EnumSportType;
    } | null;
}

class BusinessLogicHelper {
    public static isOutrightAllowed(customerSettings: ICustomerSettings, outright: IOutright) {
        if (!this.isAllowedSport(customerSettings, outright.tournament.sport.sportType)) {
            return false;
        }

        if (customerSettings.playerType !== PlayerType.B2C) {
            return !outright.isWalkinOnly;
        }

        return true;
    }

    public static isOutrightTournamentAllowed(customerSettings: ICustomerSettings, outright: IOutrightTournament) {
        return this.isAllowedSport(customerSettings, outright.sport.sportType);
    }

    public static isLeagueAllowed(customerSettings: ICustomerSettings, league: ILeague, additionalSettings: IAdditionalSettings) {
        const sportType = league.sport?.sportType;

        if (!sportType) {
            throw new Error('Sport type is undefined');
        }

        if (league.isSimulation && !this.isAllowSimulationEvents(customerSettings, sportType)) {
            return false;
        }

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return false;
        }

        const isFilteredEFootballLeague = additionalSettings.filteredEFootballLeagueIds.includes(league.id);
        if (isFilteredEFootballLeague && !this.isAllowFilteredEFootballEvents(customerSettings)) {
            return false;
        }

        return true;
    }

    public static isEventAllowed(customerSettings: ICustomerSettings, event: IEvent, additionalSettings: IAdditionalSettings) {
        const sportType = event.tournament?.sport?.sportType;

        if (!sportType) {
            throw new Error('Sport type is undefined');
        }

        if (event.isSimulation && !this.isAllowSimulationEvents(customerSettings, sportType)) {
            return false;
        }

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return false;
        }

        const isFilteredEFootballEvent = additionalSettings.filteredEFootballLeagueIds.includes(event?.tournament?.id ?? 0);
        if (isFilteredEFootballEvent && !this.isAllowFilteredEFootballEvents(customerSettings)) {
            return false;
        }

        return true;
    }

    public static filterEventCount(
        eventInfo: IEventInfo,
        customerSettings: ICustomerSettings,
        sportType: EnumSportType,
    ) {
        const eventCount = eventInfo.eventCount;

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return 0;
        }

        if (!this.isAllowSimulationEvents(customerSettings, sportType)) {
            return eventCount - eventInfo.simulationEventCount;
        }

        if (!this.isAllowFilteredEFootballEvents(customerSettings)) {
            return eventCount - eventInfo.filterEFootballEventCount;
        }

        return eventCount;
    }

    public static isAllowedSport(customerSettings: ICustomerSettings, sportType: EnumSportType) {
        if (sportType === EnumSportType.Cricket) {
            return customerSettings.isCricketEnabled;
        }

        return true;
    }

    private static isAllowFilteredEFootballEvents(customerSettings: ICustomerSettings) {
        return !customerSettings.isInEFootballLeagueBlackList;
    }

    private static isAllowSimulationEvents(customerSettings: ICustomerSettings, sportType: EnumSportType) {
        if (sportType === EnumSportType.Soccer) {
            return customerSettings.isEFootballEnabled && customerSettings.isSimulationEnabled;
        }

        return customerSettings.isSimulationEnabled;
    }
}

export default BusinessLogicHelper;
